import React, { useEffect, useState, forwardRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import MaterialTable, { serialNumberColumn } from '../components/MaterialTableAdapter'
import { toast } from 'react-toastify'
import moment from 'moment'
import { Activity, Boxes, CalendarDays, Eye, Layers, RefreshCw, ShieldAlert, X } from 'lucide-react'

import Loading from '../components/Loading'
import AddBox from '@mui/icons-material/AddBox'
import ArrowDownward from '@mui/icons-material/ArrowDownward'
import Check from '@mui/icons-material/Check'
import ChevronLeft from '@mui/icons-material/ChevronLeft'
import ChevronRight from '@mui/icons-material/ChevronRight'
import Clear from '@mui/icons-material/Clear'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import Edit from '@mui/icons-material/Edit'
import FilterList from '@mui/icons-material/FilterList'
import FirstPage from '@mui/icons-material/FirstPage'
import LastPage from '@mui/icons-material/LastPage'
import Remove from '@mui/icons-material/Remove'
import SaveAlt from '@mui/icons-material/SaveAlt'
import Search from '@mui/icons-material/Search'
import ViewColumn from '@mui/icons-material/ViewColumn'

import { getAuditLogs, getAuditLogById } from '../actions/auditTrailActions'
import { GET_AUDIT_LOG_BY_ID_RESET } from '../constants/auditTrailConstants'
import ScreenHeader from '../components/layout/ScreenHeader'
import { Button, FilterCard, FilterToggle } from '../ui'

const tableIcons = {
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => <ChevronLeft {...props} ref={ref} />),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowDownward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
}

// Action type options
const actionOptions = [
  { value: '', label: 'All Actions' },
  { value: 'CREATE', label: 'Create' },
  { value: 'UPDATE', label: 'Update' },
  { value: 'DELETE', label: 'Delete' },
  { value: 'VIEW', label: 'View' },
  { value: 'LOGIN', label: 'Login' },
  { value: 'LOGOUT', label: 'Logout' },
  { value: 'APPROVE', label: 'Approve' },
  { value: 'REJECT', label: 'Reject' },
  { value: 'ASSIGN', label: 'Assign' },
  { value: 'UNASSIGN', label: 'Unassign' },
  { value: 'EXPORT', label: 'Export' },
  { value: 'BAN', label: 'Ban' },
  { value: 'UNBAN', label: 'Unban' },
  { value: 'FREEZE', label: 'Freeze' },
  { value: 'UNFREEZE', label: 'Unfreeze' },
  { value: 'REMOVE', label: 'Remove' },
]

// Resource type options
const resourceOptions = [
  { value: '', label: 'All Resources' },
  { value: 'ROLE', label: 'Role' },
  { value: 'ADMIN', label: 'Admin' },
  { value: 'TRANSACTION', label: 'Transaction' },
  { value: 'CLUB', label: 'Club' },
  { value: 'LOCATION', label: 'Location' },
  { value: 'PLAYER', label: 'Player' },
  { value: 'SESSION', label: 'Session' },
  { value: 'MESSAGE', label: 'Message' },
  { value: 'KYC', label: 'KYC' },
  { value: 'CUSTOMER', label: 'Customer' },
]

// Severity options
const severityOptions = [
  { value: '', label: 'All Severities' },
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
  { value: 'CRITICAL', label: 'Critical' },
]

// Module options (matches sidebar menu exactly)
const moduleOptions = [
  { value: '', label: 'All Modules' },
  { value: 'Admin Management', label: 'Admin Management' },
  { value: 'Venue Management', label: 'Venue Management' },
  { value: 'Administration', label: 'Administration' },
  { value: 'Transaction Management', label: 'Transaction Management' },
  { value: 'Reports & Analytics', label: 'Reports & Analytics' },
  { value: 'Messaging', label: 'Messaging' },
  { value: 'Dealer Management', label: 'Dealer Management' },
]

// Severity badge colors
const getSeverityBadge = (severity) => {
  const colors = {
    LOW: 'bg-gray-100 text-gray-800',
    MEDIUM: 'bg-blue-100 text-blue-800',
    HIGH: 'bg-orange-100 text-orange-800',
    CRITICAL: 'bg-red-100 text-red-800',
  }
  return colors[severity] || 'bg-gray-100 text-gray-800'
}

// Module badge colors - matches sidebar menu
const getModuleBadge = (module) => {
  const colors = {
    'Dashboard': 'bg-indigo-100 text-indigo-800',
    'Admin Management': 'bg-blue-100 text-blue-800',
    'Rake Management': 'bg-yellow-100 text-yellow-800',
    'Venue Management': 'bg-emerald-100 text-emerald-800',
    'Administration': 'bg-purple-100 text-purple-800',
    'QR Tools': 'bg-pink-100 text-pink-800',
    'Transaction Management': 'bg-orange-100 text-orange-800',
    'Reports & Analytics': 'bg-cyan-100 text-cyan-800',
    'Messaging': 'bg-teal-100 text-cm-text',
    'Dealer Management': 'bg-rose-100 text-rose-800',
  }
  return colors[module] || 'bg-gray-100 text-gray-800'
}

// Calculate module from resource type (for old records without module field)
const getModuleFromResource = (resource) => {
  const moduleMap = {
    // Dashboard
    DASHBOARD: 'Dashboard',
    // Admin Management (Active Users, Customers, Player operations)
    USER: 'Admin Management',
    ADMIN: 'Admin Management',
    CUSTOMER: 'Admin Management',
    PLAYER: 'Admin Management',  // Player ban/view/freeze is in Admin Management
    KYC: 'Admin Management',
    // Rake Management
    RAKE: 'Rake Management',
    // Venue Management
    CLUB: 'Venue Management',
    LOCATION: 'Venue Management',
    // Administration
    ROLE: 'Administration',
    PERMISSION: 'Administration',
    AUDIT_LOG: 'Administration',
    // Transaction Management
    TRANSACTION: 'Transaction Management',
    WALLET: 'Transaction Management',
    PAYMENT: 'Transaction Management',
    // Reports & Analytics
    ANALYTICS: 'Reports & Analytics',
    AFFILIATE: 'Reports & Analytics',
    // Messaging
    MESSAGE: 'Messaging',
    // Chip Inventory (under Venue Management)
    CHIP_INVENTORY: 'Venue Management',
    // Dealer Management (Table Management, Time Tracking - not player operations)
    DEALER: 'Dealer Management',
    SESSION: 'Dealer Management',
  }
  return moduleMap[resource] || resource
}

// Action badge colors
const getActionBadge = (action) => {
  const colors = {
    CREATE: 'bg-green-100 text-green-800',
    UPDATE: 'bg-blue-100 text-blue-800',
    DELETE: 'bg-red-100 text-red-800',
    VIEW: 'bg-gray-100 text-gray-800',
    LOGIN: 'bg-purple-100 text-purple-800',
    LOGOUT: 'bg-purple-100 text-purple-800',
    APPROVE: 'bg-green-100 text-green-800',
    REJECT: 'bg-red-100 text-red-800',
    ASSIGN: 'bg-blue-100 text-blue-800',
    UNASSIGN: 'bg-orange-100 text-orange-800',
    EXPORT: 'bg-yellow-100 text-yellow-800',
    // New action types with distinctive colors
    BAN: 'bg-red-600 text-white',           // Dark red - stands out
    UNBAN: 'bg-green-600 text-white',       // Dark green - opposite of ban
    FREEZE: 'bg-cyan-500 text-white',       // Cyan - "frozen" effect
    UNFREEZE: 'bg-cm-gold text-cm-gold-ink',     // Teal - opposite of freeze
    REMOVE: 'bg-orange-500 text-white',     // Orange - warning action
  }
  return colors[action] || 'bg-gray-100 text-gray-800'
}

const AuditTrail = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  // States
  const [loading, setLoading] = useState(true)
  const [selectedLog, setSelectedLog] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [filtersOpen, setFiltersOpen] = useState(false)
  
  // Filter states
  const [filters, setFilters] = useState({
    action: '',
    resource: '',
    severity: '',
    module: '',
    startDate: '',
    endDate: '',
    search: '',
    page: 1,
    limit: 20,
  })

  // Redux selectors
  const getRoleInfo = useSelector((state) => state.getRoleInfo)
  const { getRoleData } = getRoleInfo

  const auditLogsState = useSelector((state) => state.auditLogs)
  const { loading: logsLoading, logs, total, page, pages, error } = auditLogsState

  const auditLogDetailState = useSelector((state) => state.auditLogDetail)
  const { loading: detailLoading, log: logDetail } = auditLogDetailState

  // Check permissions
  useEffect(() => {
    if (getRoleData) {
      if (!getRoleData.auditTrailView && !getRoleData.isSuperAdmin) {
        toast('Not Authorized', {
          type: 'error',
          hideProgressBar: true,
          autoClose: 2000,
        })
        navigate('/dashboard')
      }
    }
  }, [getRoleData, navigate])

  // Fetch audit logs
  useEffect(() => {
    dispatch(getAuditLogs(filters))
  }, [dispatch, filters])

  // Handle loading state
  useEffect(() => {
    if (!logsLoading && logs) {
      setLoading(false)
    }
    if (error) {
      setLoading(false)
      toast(error, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [logsLoading, logs, error])

  // Handle log detail
  useEffect(() => {
    if (logDetail && !detailLoading) {
      setSelectedLog(logDetail)
      setShowModal(true)
    }
  }, [logDetail, detailLoading])

  // Handle filter change
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      page: 1, // Reset page when filters change
    }))
  }

  // Confirm a manual refresh once the request settles
  useEffect(() => {
    if (refreshing && !logsLoading) {
      setRefreshing(false)
      if (!error) {
        toast('Audit trail refreshed', {
          type: 'success',
          hideProgressBar: true,
          autoClose: 1500,
        })
      }
    }
  }, [refreshing, logsLoading, error])

  // Handle refresh
  const handleRefresh = () => {
    setRefreshing(true)
    dispatch(getAuditLogs(filters))
  }

  // Handle row click
  const handleRowClick = (log) => {
    dispatch(getAuditLogById(log._id))
  }

  // Close modal
  const handleCloseModal = () => {
    setShowModal(false)
    setSelectedLog(null)
    dispatch({ type: GET_AUDIT_LOG_BY_ID_RESET })
  }

  // Table columns
  const columns = [
    serialNumberColumn,
    {
      field: 'performedByName',
      title: 'User',
      render: (rowData) => (
        <div className="font-medium text-gray-900">
          {rowData.performedByName || 'Unknown'}
          {rowData.performedByRole && (
            <span className="block text-xs text-gray-500">{rowData.performedByRole}</span>
          )}
        </div>
      ),
    },
    {
      field: 'module',
      title: 'Module',
      render: (rowData) => {
        // Use stored module or calculate from resource (for old records)
        const displayModule = rowData.module || getModuleFromResource(rowData.resource)
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getModuleBadge(displayModule)}`}>
            {displayModule}
          </span>
        )
      },
    },
    {
      field: 'action',
      title: 'Action',
      render: (rowData) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getActionBadge(rowData.action)}`}>
          {rowData.action}
        </span>
      ),
    },
    {
      field: 'resource',
      title: 'Resource',
      render: (rowData) => (
        <div>
          <span className="font-medium">{rowData.resource}</span>
          {rowData.resourceName && (
            <span className="block text-xs text-gray-500">{rowData.resourceName}</span>
          )}
        </div>
      ),
    },
    {
      field: 'ipAddress',
      title: 'IP Address',
      render: (rowData) => (
        <span className="text-sm text-gray-600">{rowData.ipAddress || '-'}</span>
      ),
    },
    {
      field: 'createdAt',
      title: 'Timestamp',
      render: (rowData) => (
        <div className="text-sm">
          <div>{moment(rowData.createdAt).format('DD-MM-YYYY')}</div>
          <div className="text-gray-500">{moment(rowData.createdAt).format('HH:mm:ss')}</div>
        </div>
      ),
    },
    {
      field: 'actions',
      title: 'View',
      sorting: false,
      headerClassName: 'action-column-header',
      bodyClassName: 'action-column-cell',
      headerStyle: { textAlign: 'center', width: 90, minWidth: 90 },
      render: (rowData) => (
        <button
          onClick={() => handleRowClick(rowData)}
          className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200 transition-all hover:-translate-y-0.5 hover:bg-blue-100 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          title="View Details"
          aria-label="View audit details"
        >
          <Eye size={17} strokeWidth={2.2} />
        </button>
      ),
      cellStyle: { textAlign: 'center', width: 90, minWidth: 90 },
    },
  ]

  const optionLabel = (options, value) => options.find((o) => o.value === value)?.label || value
  const filterChips = [
    filters.action && { icon: Activity, label: optionLabel(actionOptions, filters.action) },
    filters.resource && { icon: Boxes, label: optionLabel(resourceOptions, filters.resource) },
    filters.severity && { icon: ShieldAlert, label: optionLabel(severityOptions, filters.severity) },
    filters.module && { icon: Layers, label: optionLabel(moduleOptions, filters.module) },
    (filters.startDate || filters.endDate) && {
      icon: CalendarDays,
      label: `${filters.startDate || '…'} → ${filters.endDate || '…'}`,
    },
  ].filter(Boolean)

  if (loading) {
    return <Loading />
  }

  return (
    <div className="w-full h-full">
      <ScreenHeader
        title="Audit trail"
        actions={
          <>
            <FilterToggle
              open={filtersOpen}
              onToggle={() => setFiltersOpen((o) => !o)}
              count={filterChips.length}
              controls="audit-filters"
            />
            <Button variant="secondary" icon={RefreshCw} loading={logsLoading} onClick={handleRefresh}>
              {logsLoading ? 'Refreshing…' : 'Refresh'}
            </Button>
          </>
        }
      />

      {/* Filters */}
      <FilterCard
        id="audit-filters"
        className="my-4"
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        chips={filterChips}
        emptyLabel="All actions, resources and dates"
      >
        <div className="flex flex-wrap gap-4 items-end">
          {/* Action Filter */}
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 mb-1">Action</label>
            <select
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {actionOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Resource Filter */}
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 mb-1">Resource</label>
            <select
              value={filters.resource}
              onChange={(e) => handleFilterChange('resource', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {resourceOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Severity Filter */}
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 mb-1">Severity</label>
            <select
              value={filters.severity}
              onChange={(e) => handleFilterChange('severity', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {severityOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Module Filter */}
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 mb-1">Module</label>
            <select
              value={filters.module}
              onChange={(e) => handleFilterChange('module', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {moduleOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Start Date */}
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* End Date */}
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

        </div>
      </FilterCard>

      {/* Table */}
      <MaterialTable
        icons={tableIcons}
        columns={columns}
        data={logs || []}
        isLoading={logsLoading}
        options={{
          exportButton: false,
          search: true,
          exportAllData: false,
          rowStyle: {
            height: '5px',
            fontSize: 13,
          },
          paging: true,
          pageSize: 20,
          emptyRowsWhenPaging: false,
          pageSizeOptions: [10, 20, 50, 100],
          headerStyle: {
            position: 'sticky',
            top: '0',
            backgroundColor: 'var(--cm-surface-2)',
            fontWeight: 600,
          },
        }}
        onSearchChange={(searchText) => {
          handleFilterChange('search', searchText)
        }}
      />

      {/* Detail Modal */}
      {showModal && selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-xl font-semibold text-gray-800">Audit Log Details</h2>
              <button
                type="button"
                onClick={handleCloseModal}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-300"
                title="Close"
                aria-label="Close audit details"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4 space-y-4">
              {/* User Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Performed By</label>
                  <p className="text-gray-900 font-medium">{selectedLog.performedByName}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Role</label>
                  <p className="text-gray-900">{selectedLog.performedByRole || '-'}</p>
                </div>
              </div>

              {/* Action & Resource */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Action</label>
                  <p>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getActionBadge(selectedLog.action)}`}>
                      {selectedLog.action}
                    </span>
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Resource</label>
                  <p className="text-gray-900">{selectedLog.resource}</p>
                </div>
              </div>

              {/* Resource Details */}
              {selectedLog.resourceName && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Resource Name</label>
                  <p className="text-gray-900">{selectedLog.resourceName}</p>
                </div>
              )}

              {/* Severity & Status */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Severity</label>
                  <p>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityBadge(selectedLog.severity)}`}>
                      {selectedLog.severity}
                    </span>
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Status</label>
                  <p className={`font-medium ${selectedLog.status === 'SUCCESS' ? 'text-green-600' : 'text-red-600'}`}>
                    {selectedLog.status}
                  </p>
                </div>
              </div>

              {/* IP & Timestamp */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">IP Address</label>
                  <p className="text-gray-900">{selectedLog.ipAddress || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Timestamp</label>
                  <p className="text-gray-900">
                    {moment(selectedLog.createdAt).format('DD-MM-YYYY HH:mm:ss')}
                  </p>
                </div>
              </div>

              {/* Notes */}
              {selectedLog.notes && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Notes</label>
                  <p className="text-gray-900 bg-gray-50 p-3 rounded">{selectedLog.notes}</p>
                </div>
              )}

              {/* Changed Fields */}
              {selectedLog.changedFields && selectedLog.changedFields.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-500 mb-2 block">Changed Fields</label>
                  <div className="bg-gray-50 rounded p-3 space-y-2">
                    {selectedLog.changedFields.map((change, index) => (
                      <div key={index} className="border-b border-gray-200 pb-2 last:border-0 last:pb-0">
                        <span className="font-medium text-gray-700">{change.field}</span>
                        <div className="grid grid-cols-2 gap-2 mt-1 text-sm">
                          <div>
                            <span className="text-red-600">Old: </span>
                            <span className="text-gray-600">
                              {typeof change.oldValue === 'object' 
                                ? JSON.stringify(change.oldValue) 
                                : String(change.oldValue ?? '-')}
                            </span>
                          </div>
                          <div>
                            <span className="text-green-600">New: </span>
                            <span className="text-gray-600">
                              {typeof change.newValue === 'object' 
                                ? JSON.stringify(change.newValue) 
                                : String(change.newValue ?? '-')}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* User Agent */}
              {selectedLog.userAgent && (
                <div>
                  <label className="text-sm font-medium text-gray-500">User Agent</label>
                  <p className="text-gray-600 text-sm break-all">{selectedLog.userAgent}</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end px-6 py-4 border-t bg-gray-50">
              <button
                onClick={handleCloseModal}
                className="bg-gray-500 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AuditTrail
